"use client";

import * as React from "react";
import { cn } from "../lib/utils";

// ============ Drawer Root ============

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function Drawer({ open, onOpenChange, children }: DrawerProps) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 animate-[fadeIn_0.2s_ease-out]"
        onClick={() => onOpenChange(false)}
      />
      {children}
    </div>
  );
}

// ============ Drawer Content ============

export interface DrawerContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: "right" | "left";
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
};

export const DrawerContent = React.forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ className, side = "right", size = "lg", children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "fixed top-0 bottom-0 z-50 flex w-full flex-col bg-card shadow-xl",
        sizeMap[size],
        side === "right"
          ? "right-0 animate-[slideInRight_0.3s_ease-out]"
          : "left-0 animate-[slideInLeft_0.3s_ease-out]",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
DrawerContent.displayName = "DrawerContent";

// ============ Drawer Header ============

export const DrawerHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-between border-b border-border px-6 py-4",
      className
    )}
    {...props}
  />
));
DrawerHeader.displayName = "DrawerHeader";

// ============ Drawer Title ============

export const DrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

// ============ Drawer Body ============

export const DrawerBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("min-h-0 flex-1 overflow-y-auto px-6 py-4", className)}
    {...props}
  />
));
DrawerBody.displayName = "DrawerBody";

// ============ Drawer Footer ============

export const DrawerFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-3 border-t border-border px-6 py-4",
      className
    )}
    {...props}
  />
));
DrawerFooter.displayName = "DrawerFooter";

// ============ Drawer Close Button ============

export function DrawerClose({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}
